import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, RefreshCw } from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';

/**
 * Banner globale di impersonazione — fissato in alto, sempre visibile
 * mentre l'admin opera come un cliente impersonato.
 *
 * Layout: sfondo rosso, nome compagnia a sinistra, 2 azioni a destra:
 *   • Termina → ``stopImpersonation()`` + ricarica la pagina corrente.
 *   • Cambia  → naviga a ``/admin/impersona`` (senza terminare lo swap)
 *               così l'admin può scegliere un altro cliente.
 */
export function ImpersonationBanner() {
  const { impersonatedClient, stopImpersonation, isImpersonating } = useAdmin();
  const navigate = useNavigate();

  // Imposta una CSS variable globale (``--impersonation-banner-h``)
  // con l'altezza fissa del banner (44px) quando attivo. L'header
  // sticky la legge come ``top`` per posizionarsi SOTTO il banner.
  React.useEffect(() => {
    const root = document.documentElement;
    if (impersonatedClient) {
      root.style.setProperty('--impersonation-banner-h', '44px');
    } else {
      root.style.removeProperty('--impersonation-banner-h');
    }
    return () => {
      root.style.removeProperty('--impersonation-banner-h');
    };
  }, [impersonatedClient]);

  if (!impersonatedClient) return null;

  const handleStop = async () => {
    try {
      await stopImpersonation();
    } finally {
      // Ricarica forzata: garantisce che TUTTE le cache (catalogo,
      // carrello, budget) tornino allo stato admin senza stale data.
      window.location.reload();
    }
  };

  const handleChange = () => {
    navigate('/admin/impersona');
  };

  return (
    <div
      className="sticky top-0 z-[60] w-full bg-red-600 text-white px-4 flex items-center justify-between text-sm flex-shrink-0 shadow"
      style={{ height: '44px' }}
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Eye className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">
          <span className="font-medium">Stai impersonando:</span>{' '}
          <strong data-testid="impersonation-banner-name">{impersonatedClient.name}</strong>
          <span className="opacity-80 ml-1 font-mono text-xs hidden sm:inline">
            ({impersonatedClient.anConto})
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
        <button
          onClick={handleChange}
          disabled={isImpersonating}
          className="h-7 px-3 rounded-md bg-white/15 hover:bg-white/25 text-xs font-medium transition-colors disabled:opacity-50"
          data-testid="impersonation-change-btn"
        >
          Cambia
        </button>
        <button
          onClick={handleStop}
          disabled={isImpersonating}
          className="h-7 px-3 rounded-md bg-white text-red-700 hover:bg-red-50 text-xs font-semibold transition-colors flex items-center gap-1 disabled:opacity-50"
          data-testid="impersonation-stop-btn"
        >
          <RefreshCw className={`w-3 h-3 ${isImpersonating ? 'animate-spin' : ''}`} />
          Termina
        </button>
      </div>
    </div>
  );
}
